const cities = [
  { name: "Sarasota", zips: "34236" },
  { name: "Bradenton", zips: "34205" },
  { name: "Lakewood Ranch", zips: "34202" },
  { name: "Venice", zips: "34285" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home, studio, or campus in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function SarasotaLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white pt-6 md:pt-8 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Sarasota &amp; the Cultural Coast
          </h2>
        </div>

        {/* 300–500 word intro block */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Sarasota, Florida occupies a unique position in the state's travel landscape. This is not a luxury resort town like Naples, nor a military and logistics hub like Jacksonville — Sarasota is Florida's cultural capital, a community where the Sarasota Ballet tours internationally, the Asolo Repertory Theatre hosts visiting artists from London and New York, and Ringling College of Art and Design sends students to study abroad in Florence, Paris, and Tokyo every semester. The travel patterns here are driven by arts and culture: gallery tours through the South of France, performing arts festivals in Edinburgh, design research in Barcelona, and seasonal cultural pilgrimages that follow the international arts calendar rather than the typical beach vacation schedule.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The seasonal resident dynamic in Sarasota carries a distinctly cultural character. Snowbirds here are not just escaping winter — they are following the symphony season, the opera calendar, and the international film festival circuit. Many maintain second residences specifically to be near the Van Wezel Performing Arts Hall, the Sarasota Opera House, and the Ringling Museum's programming. When these residents discover an expired passport while preparing for a March trip to the Venice Biennale or a May gallery opening in Santa Fe, standard post office processing simply does not accommodate their timeline. The same urgency applies to parents of Ringling College students who realize their child's passport expires mid-semester abroad, or Sarasota Orchestra musicians preparing for an international tour.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The beach communities — Siesta Key, Longboat Key, Lido Key — add another layer of travel sophistication. These are not typical spring break destinations. Residents here are retired academics, former museum curators, gallery owners, and arts patrons who travel internationally with the same frequency others drive to Orlando. A last-minute booking for the Edinburgh Festival Fringe, a spontaneous decision to attend Documenta in Kassel, or an invitation to a private gallery preview in Chelsea — these are the norm, not the exception. And because these travelers expect the same cultural sophistication in their service providers that they demand from their arts institutions, they rightfully reject the idea of waiting in a government line or hoping a mail-in application arrives on time.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Fast Passport Center was built for exactly this traveler. We provide the same level of care and attention to your passport that Sarasota's cultural institutions bring to their programming: direct communication with a dedicated agent who understands the urgency of a touring artist's schedule, professional document review before anything is submitted, and processing as fast as 24 hours for true emergencies. No lines. No guesswork. Just the dependable, culturally aware service this community deserves.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Info + How It Works */}
          <div>
            {/* FPC Info Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex flex-col xl:flex-row gap-6">
                {/* Info side */}
                <div className="flex-1">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Sarasota Office</p>
                  <p className="text-white/60 text-xs mt-0.5">1819 Main St, Sarasota, FL 34236</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                By appointment only — sign up on our website first to schedule
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Walk-ins are not accepted. Schedule online then visit our downtown Sarasota office.</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, schedule your appointment to visit our office at <strong className="text-white">1819 Main St in downtown Sarasota</strong>. Appointments are required — no walk-ins. Or choose <strong className="text-white">overnight shipping</strong> with our prepaid label from any UPS/FedEx location if you prefer not to visit in person.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

                {/* Map side */}
                <div className="xl:w-[340px] flex-shrink-0">
                  <div className="rounded-lg overflow-hidden border border-white/10" style={{ height: '260px' }}>
                    <iframe
                      src="https://maps.google.com/maps?q=1819+Main+St,+Sarasota,+FL+34236&t=&z=15&ie=UTF8&iwloc=&output=embed"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Fast Passport Center Sarasota Office Location"
                      aria-label="Google Map showing Fast Passport Center Sarasota office at 1819 Main St"
                    />
                  </div>
                  <p className="text-white/40 text-xs mt-2 text-center">
                    1819 Main St, Sarasota, FL 34236
                  </p>
                </div>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Sarasota and Manatee counties — start online, ship overnight, and we handle the rest. No need to drive to Tampa or Orlando.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Tampa", href: "/tampa" },
                    { name: "St. Petersburg", href: "/st-petersburg" },
                    { name: "Venice", href: "/venice" },
                    { name: "Clearwater", href: "/clearwater" },
                    { name: "Naples", href: "/naples" },
                    { name: "Fort Myers", href: "/fort-myers" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Sarasota Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}